import React, { useEffect, useRef } from "react";
import { View, StyleSheet, Text, ScrollView, Pressable, Animated, Easing } from "react-native";
import WorkspaceCard, { WorkspaceCardProps } from "../WorkspaceCard/WorkspaceCard";
import { colors, radii, spacing } from "../../../styles/design-system";
import { AxioLogo } from "../../svgs/logos";
import { useWorkspaceThreads } from "../../../contexts/WorkspaceThreadsContext";
import { PanelLeft } from "lucide-react";

export interface WorkspaceSidebarWorkspace extends Omit<WorkspaceCardProps, "onPress" | "onThreadsPress"> {
  id: string;
}

export interface WorkspaceSidebarFooterItem {
  id: string;
  label: string;
}

export interface WorkspaceSidebarProps {
  generalWorkspaces: WorkspaceSidebarWorkspace[];
  courseWorkspaces: WorkspaceSidebarWorkspace[];
  supportWorkspaces: WorkspaceSidebarWorkspace[];
  todoWorkspaces: WorkspaceSidebarWorkspace[];
  settingsWorkspaces: WorkspaceSidebarWorkspace[];
  activeWorkspaceId?: string;
  onWorkspacePress?: (id: string) => void;
  onWorkspaceThreadsPress?: (id: string) => void;
  onFooterItemPress?: (id: string) => void;
}

export const EXPANDED_WIDTH = 272;
export const COLLAPSED_WIDTH = 67;

const WorkspaceSidebar: React.FC<WorkspaceSidebarProps> = ({
  generalWorkspaces,
  courseWorkspaces,
  supportWorkspaces,
  todoWorkspaces,
  settingsWorkspaces,
  activeWorkspaceId,
  onWorkspacePress,
  onWorkspaceThreadsPress,
  onFooterItemPress,
}) => {
  const { isSidebarCollapsed, setIsSidebarCollapsed } = useWorkspaceThreads();
  const animatedWidth = useRef(new Animated.Value(isSidebarCollapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH)).current;

  useEffect(() => {
    Animated.timing(animatedWidth, {
      toValue: isSidebarCollapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH,
      duration: 300,
      easing: Easing.inOut(Easing.ease),
      useNativeDriver: false,
    }).start();
  }, [isSidebarCollapsed, animatedWidth]);

  const renderWorkspaceSection = (label: string, workspaces: WorkspaceSidebarWorkspace[]) => {
    if (!workspaces.length) return null;

    const shouldShowLabel = !isSidebarCollapsed && label !== "GENERAL";

    return (
      <View style={styles.sectionCards}>
        {shouldShowLabel && <Text style={styles.sectionLabel}>{label}</Text>}
        {workspaces.map((workspace) => (
          <View key={workspace.id} style={styles.cardWrapper}>
            <WorkspaceCard
              {...workspace}
              isActive={workspace.id === activeWorkspaceId}
              onPress={onWorkspacePress ? () => onWorkspacePress(workspace.id) : undefined}
              onThreadsPress={onWorkspaceThreadsPress ? () => onWorkspaceThreadsPress(workspace.id) : undefined}
            />
          </View>
        ))}
      </View>
    );
  };

  return (
    <Animated.View style={[styles.container, { width: animatedWidth }]}>
      <View style={styles.logoSection}>
        <AxioLogo height={28} />
        <Pressable onPress={() => setIsSidebarCollapsed(!isSidebarCollapsed)}>
          <PanelLeft size={20} />
        </Pressable>
      </View>

      <ScrollView style={styles.scrollArea} contentContainerStyle={styles.scrollContent}>
        {renderWorkspaceSection("GENERAL", generalWorkspaces)}
        {renderWorkspaceSection("COURSE ASSISTANTS", courseWorkspaces)}
        {renderWorkspaceSection("SUPPORT", supportWorkspaces)}
      </ScrollView>

      <View style={styles.footer}>
        {todoWorkspaces.map((item) => (
          <WorkspaceCard
            key={item.id}
            {...item}
            isActive={item.id === activeWorkspaceId}
            onPress={onWorkspacePress ? () => onWorkspacePress(item.id) : undefined}
          />
        ))}
        {settingsWorkspaces.map((item) => (
          <WorkspaceCard
            key={item.id}
            {...item}
            isActive={item.id === activeWorkspaceId}
            onPress={onWorkspacePress ? () => onWorkspacePress(item.id) : undefined}
          />
        ))}
      </View>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: 272,
    borderRightWidth: 1,
    borderRightColor: colors.grey[200],
    justifyContent: "space-between",
  },
  logoSection: {
    height: 57,
    paddingHorizontal: 24,
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    backgroundColor: "#fff",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  scrollArea: {
    flexGrow: 1,
  },
  scrollContent: {
    paddingBottom: spacing.lg,
  },
  sectionLabel: {
    fontSize: 12,
    fontWeight: "600",
    letterSpacing: 0.8,
    textTransform: "uppercase",
    color: colors.grey[500],
    marginBottom: spacing.xs,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  sectionCards: {
    marginTop: spacing.xs,
    padding: spacing.xs,
  },
  cardWrapper: {
    gap: 4,
  },
  footer: {
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
    padding: spacing.xs,
  },
});

export default WorkspaceSidebar;
